/**
 * Resume Studio — multi-entry education row.
 *
 * Renders one <EducationBlock/> per row of the fresher/experienced
 * wizard's education step. Supports 10th/12th/UG/PG/PhD/Diploma with
 * conditional field visibility.
 */
import React from "react";
import { Trash } from "@phosphor-icons/react";
import { Field } from "./shared";

const EDU_LEVEL_OPTIONS = [
    "10th",
    "12th",
    "Diploma",
    "Undergraduate",
    "Postgraduate",
    "PhD",
    "Certification",
];

export default function EducationBlock({ idx, edu, testidPrefix, onChange, onRemove, accentColor }) {
    const borderCls = accentColor === "purple" ? "border-purple-500" : "border-cobalt-500";
    const yearsInvalid =
        edu.from_year && edu.to_year && /^\d+$/.test(edu.from_year) && /^\d+$/.test(edu.to_year)
            && parseInt(edu.to_year, 10) < parseInt(edu.from_year, 10);
    const scoreInvalid = (() => {
        if (!edu.score) return false;
        const n = Number(edu.score);
        if (Number.isNaN(n)) return true;
        if (edu.score_type === "cgpa" && (n < 0 || n > 10)) return true;
        if (edu.score_type === "percentage" && (n < 0 || n > 100)) return true;
        return false;
    })();

    const isSchoolLevel = edu.level === "10th" || edu.level === "12th";

    return (
        <div className={`mt-4 border-l-2 ${borderCls} pl-3 pb-2`}>
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500">
                    Education #{idx + 1}
                </span>
                {onRemove && (
                    <button
                        onClick={onRemove}
                        data-testid={`${testidPrefix}-remove-${idx}`}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="Remove education"
                    >
                        <Trash size={14} />
                    </button>
                )}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <Field label="Level">
                    <select
                        data-testid={`${testidPrefix}-level-${idx}`}
                        value={edu.level || ""}
                        onChange={(e) => onChange("level", e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white"
                    >
                        <option value="" disabled>
                            Select level
                        </option>
                        {EDU_LEVEL_OPTIONS.map((lvl) => (
                            <option key={lvl} value={lvl}>
                                {lvl}
                            </option>
                        ))}
                    </select>
                </Field>
                <Field label={isSchoolLevel ? "Board (CBSE, ICSE, State)" : "Degree"}>
                    <input
                        data-testid={`${testidPrefix}-degree-${idx}`}
                        value={edu.degree}
                        onChange={(e) => onChange("degree", e.target.value)}
                        placeholder={isSchoolLevel ? "CBSE / ICSE / State" : "BTech / BE / MBA"}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2"
                    />
                </Field>
                {!isSchoolLevel && (
                    <Field label="Branch / Stream">
                        <input
                            data-testid={`${testidPrefix}-branch-${idx}`}
                            value={edu.branch}
                            onChange={(e) => onChange("branch", e.target.value)}
                            placeholder="Computer Science"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2"
                        />
                    </Field>
                )}
                <Field label={isSchoolLevel ? "School" : "College / University"}>
                    <input
                        data-testid={`${testidPrefix}-college-${idx}`}
                        value={edu.college}
                        onChange={(e) => onChange("college", e.target.value)}
                        placeholder={isSchoolLevel ? "e.g. DAV Public School" : "e.g. IIT Delhi"}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2"
                    />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="From year">
                        <input
                            data-testid={`${testidPrefix}-from-${idx}`}
                            value={edu.from_year}
                            onChange={(e) => onChange("from_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="2019"
                            inputMode="numeric"
                            maxLength={4}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2"
                        />
                    </Field>
                    <Field label="To year">
                        <input
                            data-testid={`${testidPrefix}-to-${idx}`}
                            value={edu.is_current ? "Present" : edu.to_year}
                            disabled={edu.is_current}
                            onChange={(e) => onChange("to_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="2023"
                            inputMode="numeric"
                            maxLength={4}
                            className={`w-full border rounded-lg px-3 py-2 ${
                                yearsInvalid ? "border-red-500" : "border-slate-300"
                            } ${edu.is_current ? "bg-slate-100 text-slate-500" : ""}`}
                        />
                    </Field>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600 self-center">
                    <input
                        type="checkbox"
                        data-testid={`${testidPrefix}-current-${idx}`}
                        checked={!!edu.is_current}
                        onChange={(e) => onChange("is_current", e.target.checked)}
                        className="rounded"
                    />
                    Currently pursuing
                </label>
                <div className="grid grid-cols-[1fr_140px] gap-2">
                    <Field label={edu.score_type === "percentage" ? "Percentage" : "CGPA / GPA"}>
                        <input
                            data-testid={`${testidPrefix}-score-${idx}`}
                            value={edu.score}
                            onChange={(e) => onChange("score", e.target.value)}
                            placeholder={edu.score_type === "percentage" ? "e.g. 92" : "e.g. 8.6"}
                            inputMode="decimal"
                            className={`w-full border rounded-lg px-3 py-2 ${
                                scoreInvalid ? "border-red-500" : "border-slate-300"
                            }`}
                        />
                    </Field>
                    <Field label="Score type">
                        <select
                            data-testid={`${testidPrefix}-score-type-${idx}`}
                            value={edu.score_type || "cgpa"}
                            onChange={(e) => onChange("score_type", e.target.value)}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white"
                        >
                            <option value="cgpa">CGPA</option>
                            <option value="percentage">%</option>
                        </select>
                    </Field>
                </div>
            </div>
            {(yearsInvalid || scoreInvalid) && (
                <div className="mt-2 text-xs text-red-600">
                    {yearsInvalid && <div>To year must be ≥ From year.</div>}
                    {scoreInvalid && (
                        <div>
                            {edu.score_type === "cgpa"
                                ? "CGPA should be between 0 and 10."
                                : "Percentage should be between 0 and 100."}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
